/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Tue Dec 19 2023
 **/

export default class Heap<T = any> {
	constructor(protected _heap: { priority: number; value: T }[] = []) {}

	public peek() {
		return this._heap[0];
	}

	public insert(priority: number, value: T) {
		const node = { priority, value };
		this._heap.push(node);
		this.heapify_after_insert();
	}

	public remove() {
		const count = this._heap.length;
		const rootNode = this._heap[0];

		if (count <= 0) return undefined;
		if (count === 1) {
			this._heap = [];
		} else {
			this._heap[0] = this._heap.pop()!;
			this.heapify_after_remove();
		}

		return rootNode;
	}

	private left_child_index = (parentIndex: number) => parentIndex * 2 + 1;
	private right_child_index = (parentIndex: number) => parentIndex * 2 + 2;
	private parent_index = (childIndex: number) => Math.floor((childIndex - 1) / 2);

	private heapify_after_insert = () => {
		let index = this._heap.length - 1;
		const lastInsertedNode = this._heap[index];

		while (index > 0) {
			const parentIndex = this.parent_index(index);
			if (this._heap[parentIndex].priority > lastInsertedNode.priority) {
				this._heap[index] = this._heap[parentIndex];
				index = parentIndex;
			} else {
				break;
			}
		}

		this._heap[index] = lastInsertedNode;
	};

	private heapify_after_remove = () => {
		let index = 0;
		const count = this._heap.length;
		const rootNode = this._heap[index];

		while (this.left_child_index(index) < count) {
			const leftIndex = this.left_child_index(index);
			const rightIndex = this.right_child_index(index);

			const largestIndex =
				rightIndex < count && this._heap[rightIndex].priority < this._heap[leftIndex].priority
					? rightIndex
					: leftIndex;

			if (this._heap[largestIndex].priority <= rootNode.priority) {
				this._heap[index] = this._heap[largestIndex];
				index = largestIndex;
			} else {
				break;
			}
		}

		this._heap[index] = rootNode;
	};
}
